import { useState } from "react";
import { AlertTriangle, Clock, CloudOff, Store, XCircle } from "lucide-react";
import { toast } from "sonner";
import type { ListingChangePayload } from "@colab/shared";
import { EmptyState } from "@/components/shared/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/utils";
import { ListingForm } from "./listing-form";
import { ListingPreviewCard } from "./listing-preview-card";
import { usePortalListing } from "./use-portal-listing";
import { useSubmitListingChange } from "./use-submit-listing-change";

// Portal "My Listing" — the partner-facing marketplace listing editor.
//
// One query (usePortalListing, shared with the top-nav gate) discriminates
// the whole page: not configured / not linked → quiet EmptyState (the page
// is only reachable by URL in those states, the nav item self-hides);
// stale_link → warning, no form; unavailable with no pending → retry card,
// no form (nothing safe to prefill). Otherwise the form prefills from the
// pending request's payload (so a partner sees exactly what's in review)
// falling back to the live marketplace row, with a pending or declined
// banner above it. While a request is pending the form is disabled — the
// worker would 409 pending_conflict anyway.
//
// `draft` lifts the form's current values so ListingPreviewCard can render
// a live marketplace tile in the side column. All hooks sit above the early
// returns (React error #310 — no lint rule guards this repo).

export function ListingPage() {
  const listingQuery = usePortalListing();
  const submit = useSubmitListingChange();
  // null until the partner first edits; the preview falls back to `initial`.
  const [draft, setDraft] = useState<ListingChangePayload | null>(null);

  if (listingQuery.isLoading) {
    return (
      <div>
        <PageHeader />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Skeleton className="h-96 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  const data = listingQuery.data;
  if (!data || !data.configured || !data.linked) {
    return (
      <div>
        <PageHeader subline={false} />
        <EmptyState
          icon={Store}
          title="Your marketplace listing isn't set up yet"
          description="Contact the Bookboost partnerships team if you'd like your integration featured on the marketplace."
          className="max-w-xl"
        />
      </div>
    );
  }

  if (data.live_error === "stale_link") {
    return (
      <div>
        <PageHeader subline={false} />
        <div className="flex max-w-xl items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-medium">
              This listing is no longer available on the marketplace
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Contact Bookboost to get your listing re-linked.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { live, pending, last_declined } = data;

  if (data.live_error === "unavailable" && !pending) {
    return (
      <div>
        <PageHeader subline={false} />
        <div className="flex max-w-xl items-start gap-3 rounded-xl border bg-muted/20 p-5">
          <CloudOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">
              The marketplace is temporarily unavailable
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              We couldn&apos;t load your live listing. Try again shortly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // pending?.payload always exists in the unavailable branch (guarded above),
  // and live is non-null whenever live_error is null — the fallback return is
  // for the type system, not a reachable state.
  const initial = pending?.payload ?? live;
  if (!initial) return null;

  function handleSubmit(payload: ListingChangePayload) {
    setDraft(payload);
    submit.mutate(payload, {
      onSuccess: () => {
        toast.success("Changes submitted for review");
      },
    });
  }

  return (
    <div>
      <PageHeader />

      {pending ? (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-300/60 bg-amber-50/50 p-4 dark:border-amber-700/40 dark:bg-amber-950/20">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
          <div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Your changes are under review
            </p>
            <p className="mt-0.5 text-xs text-amber-800/80 dark:text-amber-300/70">
              Submitted {formatDate(pending.created_at)}. The form shows your
              submitted values; you can edit again once Bookboost has reviewed
              them.
            </p>
          </div>
        </div>
      ) : null}

      {!pending && last_declined ? (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-medium">
              Your last submission was declined
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {last_declined.review_note
                ? `“${last_declined.review_note}”`
                : "You can update your listing and submit again."}
              {last_declined.reviewed_at
                ? ` · ${formatDate(last_declined.reviewed_at)}`
                : null}
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <ListingForm
          initial={initial}
          disabled={Boolean(pending)}
          submitting={submit.isPending}
          onSubmit={handleSubmit}
          onChange={setDraft}
        />
        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Marketplace preview
          </p>
          <ListingPreviewCard payload={draft ?? initial} />
        </div>
      </div>
    </div>
  );
}

function PageHeader({ subline = true }: { subline?: boolean }) {
  return (
    <header className="mb-8">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Marketplace
      </p>
      <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
        My Listing
      </h1>
      {subline ? (
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Changes are reviewed by Bookboost before they go live.
        </p>
      ) : null}
    </header>
  );
}
